import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  keyframes,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import type { Tab } from '@revelio/data-access';

import type { GlobalAssistantAccessDecision } from './global-ai-assistant-access';
import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type { GlobalAssistantSuggestMatch } from './global-ai-assistant-api';
import { GlobalAIAssistantChartPreviewSlot } from './global-ai-assistant-chart-preview-slot';
import styles from './global-ai-assistant-drawer.module.css';
import { GlobalAssistantPermissionUpsell } from './global-ai-assistant-permission-upsell';
import {
  getSingleChartStandardPageButtonLabel,
  getStandardPageMatchForOptionId,
} from './global-ai-assistant-standard-page-match';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { evalRecordMarkerDowngrade } from './global-ai-eval-recorder';
import {
  hasOfferMarker,
  type OfferKind,
  type OfferSegment,
  parseOfferSegments,
} from './offer-marker';
import type { ReasoningTrace as ReasoningTraceType } from './reasoning-trace';

// Offer marker whose backing action can't render: plain text (never a dead
// trigger) + a dev-only eval record so the probe harness can distinguish
// "downgraded marker" from intentional plain text — visually identical.
const downgradedOfferSpan = (
  messageId: string,
  seg: Extract<OfferSegment, { type: 'offer' }>,
  segIdx: number
) => {
  evalRecordMarkerDowngrade(messageId, seg.offer, seg.text);
  return <span key={segIdx}>{seg.text}</span>;
};

export const ASSISTANT_ICON_SIZE = '21.56px';

export const CHIP_BUTTON_PROPS = {
  variant: 'outline' as const,
  fontWeight: 600,
  fontSize: '10px',
  lineHeight: '13px',
  height: 'auto',
  minH: '25px',
  py: '6px',
  px: '8px',
  whiteSpace: 'normal' as const,
};

export type ReportOfferCardFields = {
  reportType: string;
  reportName: string;
  description?: string;
  sampleLink: string;
  confidence: number;
};

export const buildReportOfferLaunchKey = (
  messageId: string,
  reportType: string
): string => `${messageId}:${reportType}`;

/**
 * Pulse for an inline report trigger whose launch (prefill extraction +
 * report-builder navigation) is in flight — the async gap is long enough that
 * a static link reads as "nothing happened". Mirrors the cards' isLoading.
 */
const reportTriggerLaunchPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

/**
 * Match an inline report trigger's visible text to the offer it NAMES.
 * Exact normalized match first, then a UNIQUE containment match (a marker
 * "Attrition" matches the sole offer named "Attrition Report"). No/ambiguous
 * match → undefined: the marker renders as plain text and the offer keeps its
 * card — a name mismatch degrades the affordance but can never wire a click
 * to a different report (the offers array may silently drop unresolvable
 * report types, so positional marker→offer mapping is unsafe).
 */
export function matchReportOfferByMarkerText<T extends { reportName: string }>(
  offers: readonly T[],
  markerText: string
): T | undefined {
  const norm = (s: string) => s.trim().toLowerCase();
  const target = norm(markerText);
  if (!target) {
    return undefined;
  }
  const exact = offers.find((offer) => norm(offer.reportName) === target);
  if (exact) {
    return exact;
  }
  const containing = offers.filter((offer) => {
    const name = norm(offer.reportName);
    return name.includes(target) || target.includes(name);
  });
  return containing.length === 1 ? containing[0] : undefined;
}

function AssistantStarIcon() {
  return (
    <Box
      as="svg"
      width="14px"
      height="16px"
      viewBox="0 0 14 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M7 0L8.5 5.5L14 7L8.5 8.5L7 16L5.5 8.5L0 7L5.5 5.5L7 0Z"
        fill="#FFFFFF"
      />
    </Box>
  );
}

export function AssistantAvatar() {
  return (
    <Flex
      flexShrink={0}
      w={ASSISTANT_ICON_SIZE}
      h={ASSISTANT_ICON_SIZE}
      borderRadius="full"
      align="center"
      justify="center"
      className={styles.avatar}
      aria-hidden
    >
      <AssistantStarIcon />
    </Flex>
  );
}

function AssistantAvatarSpacer() {
  return (
    <Box
      flexShrink={0}
      w={ASSISTANT_ICON_SIZE}
      h={ASSISTANT_ICON_SIZE}
      aria-hidden
    />
  );
}

function AssistantAvatarSlot({ show }: { show: boolean }) {
  return show ? <AssistantAvatar /> : <AssistantAvatarSpacer />;
}

const THINKING_COLLAPSED_MAX_H = '72px';
const THINKING_EXPANDED_MAX_H = '320px';

/**
 * Live model-reasoning bubble: streamed `thinking` text in a compact, scrollable
 * box that auto-follows the latest chunk while collapsed, and toggles to a taller
 * view on click. Muted/italic so it reads as reasoning, not the answer.
 */
// `bare` drops the bubble's own left rule/indent when it is nested inside the
// reasoning trace (which already supplies a left border), avoiding a double rule.
function ThinkingBubble({ text, bare }: { text: string; bare?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // `text` is a trigger dependency: re-scroll to the latest chunk as it streams.
  // biome-ignore lint/correctness/useExhaustiveDependencies: text drives the re-scroll
  useEffect(() => {
    if (!expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text, expanded]);
  return (
    <Box
      ref={scrollRef}
      flex={1}
      minW={0}
      onClick={() => setExpanded((prev) => !prev)}
      cursor="pointer"
      maxH={expanded ? THINKING_EXPANDED_MAX_H : THINKING_COLLAPSED_MAX_H}
      overflowY="auto"
      borderLeft={bare ? undefined : '2px solid'}
      className={bare ? undefined : styles.thinkingBorder}
      pl={bare ? 0 : 2}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
      sx={{
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Text
        fontSize="12px"
        lineHeight="15px"
        className={styles.text}
        fontStyle="italic"
        opacity={0.55}
        whiteSpace="pre-wrap"
      >
        {text}
      </Text>
    </Box>
  );
}

function ReasoningTraceView({ trace }: { trace: ReasoningTraceType }) {
  const running = trace.endedAt === undefined;
  const [expanded, setExpanded] = useState(true);
  // Collapse automatically once the turn finishes.
  useEffect(() => {
    if (!running) setExpanded(false);
  }, [running]);
  const seconds = trace.endedAt
    ? Math.max(1, Math.round((trace.endedAt - trace.startedAt) / 1000))
    : null;
  return (
    <Box>
      <Box
        as="button"
        onClick={() => setExpanded((p) => !p)}
        fontSize="12px"
        className={styles.text}
        opacity={0.7}
        cursor="pointer"
        background="none"
        border="none"
        p={0}
        textAlign="left"
      >
        {running ? 'Working…' : `Worked for ${seconds}s`} {expanded ? '▾' : '▸'}
      </Box>
      {expanded && (
        <Box pl={2} borderLeft="2px solid" className={styles.thinkingBorder}>
          {trace.steps.map((s) => (
            <Box key={s.id} fontSize="12px" opacity={0.7}>
              <Flex align="center" gap={1}>
                {s.status === 'running' ? (
                  <Spinner size="xs" className={styles.text} flexShrink={0} />
                ) : (
                  <Text as="span" fontWeight="medium" className={styles.text}>
                    {s.status === 'error' ? '✕' : '✓'}
                  </Text>
                )}
                <Text fontWeight="medium" className={styles.text}>
                  {s.title}
                </Text>
              </Flex>
              {s.description && (
                <Text fontStyle="italic" opacity={0.8}>
                  {s.description}
                </Text>
              )}
              {s.thinking && <ThinkingBubble text={s.thinking} bare />}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export type TranscriptMessagesProps = {
  transcript: GlobalAiTranscriptMessage[];
  messagesById: Map<string, GlobalAiTranscriptMessage>;
  stampedOfferKindsByTargetId: ReadonlyMap<string, ReadonlySet<OfferKind>>;
  /**
   * Launch key of the report offer currently opening the report builder
   * (`buildReportOfferLaunchKey(messageId, reportType)`), or null. The clicked
   * inline trigger pulses while its launch is in flight, and all report
   * triggers are inert until it settles.
   */
  reportOfferLaunchingKey: string | null;
  isTurnRunning: boolean;
  isCommandInFlight: boolean;
  lastUserMessageId?: string;
  lastUserMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  lastUserPrompt: string | null;
  currentPathname: string;
  isInCustomDashEditMode: () => boolean;
  transcriptScrollRoot: HTMLDivElement | null;
  userTabs: Tab[];
  customDashAccess: GlobalAssistantAccessDecision;
  fireNavOffer: (
    navMsg: Extract<GlobalAiTranscriptMessage, { kind: 'nav_confirm' }>
  ) => void;
  fireBuildOffer: (
    buildMsg: Extract<
      GlobalAiTranscriptMessage,
      { kind: 'create_page_confirm' }
    >
  ) => void;
  fireStandardPageOffer: (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => void;
  getStandardPageOfferState: (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    match: GlobalAssistantSuggestMatch;
    access: GlobalAssistantAccessDecision;
  } | null;
  applySingleChartFiltersToCurrentPage: (
    msg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => void;
  /**
   * Fires the report-builder launch for an inline `offer:report` narration
   * trigger (the only report-offer surface now that the card is gone).
   */
  onReportOfferClick: (args: {
    messageId: string;
    offer: ReportOfferCardFields;
    prompt?: string;
  }) => void;
};

export function TranscriptMessages(props: TranscriptMessagesProps) {
  const {
    transcript,
    messagesById,
    stampedOfferKindsByTargetId,
    reportOfferLaunchingKey,
    isTurnRunning,
    isCommandInFlight,
    lastUserMessageId,
    lastUserMessageRef,
    transcriptScrollRoot,
    userTabs,
    customDashAccess,
    fireNavOffer,
    fireBuildOffer,
    fireStandardPageOffer,
    getStandardPageOfferState,
    onReportOfferClick,
  } = props;

  // Fire the report offer: the exact action the card's "Open report builder"
  // button invokes, shared by the inline narration trigger. Mirrors the other
  // fire handlers' mid-stream guard, plus the cards' launch guard: while a
  // report launch is in flight (prefill extraction + navigation), all report
  // triggers are inert so a click can't double-launch.
  const fireReportOffer = (
    targetMsg: Extract<GlobalAiTranscriptMessage, { kind: 'report_offers' }>,
    offer: ReportOfferCardFields
  ) => {
    if (
      isTurnRunning ||
      isCommandInFlight ||
      reportOfferLaunchingKey !== null
    ) {
      return;
    }
    onReportOfferClick({
      messageId: targetMsg.id,
      offer,
      prompt: targetMsg.prompt,
    });
  };

  const renderMessage = (
    msg: GlobalAiTranscriptMessage,
    previousMsg: GlobalAiTranscriptMessage | undefined
  ) => {
    const showAvatar = !previousMsg || previousMsg.role === 'user';

    // Resolve an inline trigger's backing message from the narration's stamp.
    // Absent/mismatched stamp → undefined → the marker renders as plain text.
    const stampedTarget = <
      K extends
        | 'nav_confirm'
        | 'create_page_confirm'
        | 'single_chart'
        | 'report_offers',
    >(
      textMsg: Extract<GlobalAiTranscriptMessage, { kind: 'text' }>,
      offer: OfferKind,
      kind: K
    ): Extract<GlobalAiTranscriptMessage, { kind: K }> | undefined => {
      const targetId = textMsg.offerTargets?.[offer];
      const target = targetId ? messagesById.get(targetId) : undefined;
      return target &&
        target.role === 'assistant' &&
        'kind' in target &&
        target.kind === kind
        ? (target as Extract<GlobalAiTranscriptMessage, { kind: K }>)
        : undefined;
    };

    // Per-chart trigger renderability predicate. Mirrors EXACTLY the gate the
    // inline offer:page trigger evaluates for a given chart, so that the
    // post-chart chip suppression ("hide the chip only when the inline trigger
    // will truly render") stays in lockstep with the trigger — including the
    // access/applicability dimension, not just the stamp. This helper is the
    // single source of truth used by BOTH the trigger branch and the chip
    // suppression sites; do not inline divergent copies.
    const pageTriggerCanRender = (
      chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
    ): boolean => !!getStandardPageOfferState(chartMsg)?.access.allowed;

    // The "Go to <page>" chip that routes to the standard page backing a single
    // chart's data source. Shared by the rendered-chart branch and by the text
    // answer that follows a single-value answer, so a text answer offers the
    // same page-navigation affordance as a chart. Returns null when the chart
    // message has no standard-page match.
    const renderSingleChartStandardPageChip = (
      chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
    ) => {
      const offer = getStandardPageOfferState(chartMsg);
      if (!offer) {
        return null;
      }
      const { access } = offer;
      return (
        <Box maxW="100%">
          <Button
            {...CHIP_BUTTON_PROPS}
            className={styles.chip}
            data-testid="global-ai-open-standard-page"
            maxW="100%"
            onClick={
              access.allowed
                ? () => {
                    // Same choke point as the inline offer:page trigger: the
                    // shared fire handler threads the answer's RESOLVED filter
                    // artifact through the navigation, so chip and trigger can
                    // never diverge (and neither re-resolves the prompt).
                    fireStandardPageOffer(chartMsg);
                  }
                : undefined
            }
            isDisabled={!access.allowed || isTurnRunning || isCommandInFlight}
          >
            {access.allowed
              ? getSingleChartStandardPageButtonLabel(chartMsg.standardPagePath)
              : `No access to ${access.featureLabel}`}
          </Button>
          {access.allowed ? null : (
            <GlobalAssistantPermissionUpsell
              access={access}
              data-testid="global-ai-single-chart-standard-page-upsell"
            />
          )}
        </Box>
      );
    };

    if (msg.role === 'user') {
      return (
        <Box
          key={msg.id}
          ref={msg.id === lastUserMessageId ? lastUserMessageRef : undefined}
          alignSelf="flex-end"
          maxW="95%"
          mb={3}
        >
          <Box
            className={styles.userBubble}
            px={2.5}
            py={1.5}
            borderWidth="1px"
          >
            <Text className={`${styles.text} ${styles.bodyText}`}>
              {msg.text}
            </Text>
          </Box>
        </Box>
      );
    }

    if (msg.kind === 'text') {
      // When this text is the narration of a single-value answer (its preceding
      // message is the headless `answerAsText` chart), render the "Go to <page>"
      // chip BELOW the answer text so a single answer gets the same page
      // affordance as a single chart, sitting under the answer rather than above.
      // The fallback "Go to <page>" button below the answer uses previousMsg
      // adjacency (a placement choice, not click wiring). The inline offer:page
      // TRIGGER resolves its chart from the narration stamp instead.
      const answerChartMsg =
        previousMsg &&
        previousMsg.role === 'assistant' &&
        previousMsg.kind === 'single_chart' &&
        previousMsg.answerAsText
          ? previousMsg
          : undefined;
      const pageTargetChart = stampedTarget(msg, 'page', 'single_chart');
      const pageOfferLive =
        !!pageTargetChart &&
        pageTriggerCanRender(pageTargetChart) &&
        hasOfferMarker(msg.text, 'page');
      // Fallback chip target: the stamped chart when the stamp resolves, else the
      // adjacent answerAsText chart. Adjacency alone is fragile — the report
      // ride-along can interleave a message between the chart and the narration —
      // so a resolvable stamp wins.
      const pageFallbackChart = pageTargetChart ?? answerChartMsg;
      const answerStandardPageChip =
        pageFallbackChart && !pageOfferLive
          ? renderSingleChartStandardPageChip(pageFallbackChart)
          : null;
      return (
        <Flex
          key={msg.id}
          direction="column"
          alignSelf="stretch"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <Flex align="flex-start" gap={2} maxW="100%">
            <AssistantAvatarSlot show={showAvatar} />
            <Text
              className={`${styles.text} ${styles.bodyText}`}
              flex={1}
              minW={0}
            >
              {parseOfferSegments(msg.text).map((seg, segIdx) => {
                if (seg.type === 'text') {
                  return <span key={segIdx}>{seg.text}</span>;
                }
                if (seg.offer === 'page') {
                  // Inline single-value page offer. Wire to the preceding
                  // single-value chart's standard page; when there's no live
                  // offer (no chart / no access), render plain text — the "Go to
                  // <page>" button below covers the no-access upsell.
                  if (!pageOfferLive || !pageTargetChart) {
                    return downgradedOfferSpan(msg.id, seg, segIdx);
                  }
                  return (
                    <Text
                      as="span"
                      key={segIdx}
                      data-testid="offer-trigger-page"
                      role="button"
                      tabIndex={0}
                      className={styles.text}
                      textDecoration="underline"
                      cursor="pointer"
                      onClick={() => fireStandardPageOffer(pageTargetChart)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fireStandardPageOffer(pageTargetChart);
                        }
                      }}
                    >
                      {seg.text}
                    </Text>
                  );
                }
                if (seg.offer === 'custom') {
                  // The post-chart custom-page offer was removed; a stray
                  // offer:custom marker renders as plain text, never a trigger.
                  return downgradedOfferSpan(msg.id, seg, segIdx);
                }
                if (seg.offer === 'report') {
                  const targetMsg = stampedTarget(
                    msg,
                    'report',
                    'report_offers'
                  );
                  // The trigger opens the offer it NAMES (the visible text IS
                  // the report name), never a positional guess — see
                  // matchReportOfferByMarkerText.
                  const offer = targetMsg
                    ? matchReportOfferByMarkerText(targetMsg.offers, seg.text)
                    : undefined;
                  if (!targetMsg || !offer) {
                    // No backing offer for this marker — plain text, never a
                    // dead trigger (mirrors the nav/build fallback).
                    return downgradedOfferSpan(msg.id, seg, segIdx);
                  }
                  // Launch feedback: the clicked trigger pulses while the
                  // prefill + report-builder navigation is in flight (the
                  // cards' isLoading equivalent); fireReportOffer makes every
                  // report trigger inert until the launch settles.
                  const isLaunching =
                    reportOfferLaunchingKey ===
                    buildReportOfferLaunchKey(targetMsg.id, offer.reportType);
                  return (
                    <Text
                      as="span"
                      key={segIdx}
                      data-testid="offer-trigger-report"
                      role="button"
                      tabIndex={0}
                      aria-busy={isLaunching}
                      aria-disabled={reportOfferLaunchingKey !== null}
                      className={styles.text}
                      textDecoration="underline"
                      cursor={isLaunching ? 'progress' : 'pointer'}
                      animation={
                        isLaunching
                          ? `${reportTriggerLaunchPulse} 1.1s ease-in-out infinite`
                          : undefined
                      }
                      onClick={() => fireReportOffer(targetMsg, offer)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fireReportOffer(targetMsg, offer);
                        }
                      }}
                    >
                      {seg.text}
                    </Text>
                  );
                }
                const offerMsg =
                  seg.offer === 'nav'
                    ? stampedTarget(msg, 'nav', 'nav_confirm')
                    : stampedTarget(msg, 'build', 'create_page_confirm');
                if (!offerMsg) {
                  // No backing offer for this marker — render as plain text so a
                  // stray/mismatched marker never becomes a dead trigger.
                  return downgradedOfferSpan(msg.id, seg, segIdx);
                }
                return (
                  <Text
                    as="span"
                    key={segIdx}
                    data-testid={
                      offerMsg.kind === 'nav_confirm'
                        ? 'offer-trigger-nav'
                        : 'offer-trigger-build'
                    }
                    role="button"
                    tabIndex={0}
                    className={styles.text}
                    textDecoration="underline"
                    cursor="pointer"
                    onClick={() =>
                      offerMsg.kind === 'nav_confirm'
                        ? fireNavOffer(offerMsg)
                        : fireBuildOffer(offerMsg)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (offerMsg.kind === 'nav_confirm') {
                          fireNavOffer(offerMsg);
                        } else {
                          fireBuildOffer(offerMsg);
                        }
                      }
                    }}
                  >
                    {seg.text}
                  </Text>
                );
              })}
            </Text>
          </Flex>
          {answerStandardPageChip ? (
            <Flex direction="column" align="flex-end" w="100%">
              {answerStandardPageChip}
            </Flex>
          ) : null}
        </Flex>
      );
    }

    if (msg.kind === 'clarification') {
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Text
            className={`${styles.text} ${styles.bodyText}`}
            flex={1}
            minW={0}
          >
            {msg.text}
          </Text>
        </Flex>
      );
    }

    if (msg.kind === 'error') {
      return (
        <Box key={msg.id} alignSelf="stretch" maxW="100%" mb={3} w="100%">
          <Alert status="error" className={styles.alert} fontSize="xs" py={2}>
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold" fontSize="11px">
                Something went wrong
              </Text>
              <Text fontSize="11px">{msg.text}</Text>
            </Box>
          </Alert>
        </Box>
      );
    }

    if (msg.kind === 'nav_confirm') {
      // The `navigate` tool is terminal: it never navigates. This chip / inline
      // trigger is the ONLY way the assistant navigates, and only on an explicit
      // click (hard rule). Mirror the single-chart standard-page access gate so
      // the chip is disabled + upsold when the account can't open the
      // destination.
      // Suppressed when a narration stamped THIS chip's id for `nav` (and
      // carried the marker) AND that stamp is renderable — the inline trigger
      // replaced it. Unstamped / non-renderable chips fall back.
      if (stampedOfferKindsByTargetId.get(msg.id)?.has('nav')) {
        return null;
      }
      // No auto-navigation anywhere: the chip renders as the manual fallback on
      // every page (home and off-home alike) when no inline trigger is present.
      const navMatch = getStandardPageMatchForOptionId(msg.optionId, {});
      const access = navMatch
        ? getGlobalAssistantRouteAccess(navMatch.navigatePath, userTabs)
        : null;
      const canNavigate = Boolean(navMatch && access?.allowed);
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Flex
            direction="column"
            align="flex-end"
            gap={2}
            flex={1}
            minW={0}
            w="100%"
          >
            <Box maxW="100%">
              <Button
                {...CHIP_BUTTON_PROPS}
                className={styles.chip}
                data-testid="global-ai-nav-confirm"
                maxW="100%"
                isDisabled={
                  !canNavigate ||
                  msg.resolved ||
                  isTurnRunning ||
                  isCommandInFlight
                }
                onClick={
                  canNavigate && navMatch ? () => fireNavOffer(msg) : undefined
                }
              >
                {access && !access.allowed
                  ? `No access to ${access.featureLabel}`
                  : `Go to ${msg.pageName}`}
              </Button>
              {access && !access.allowed ? (
                <GlobalAssistantPermissionUpsell
                  access={access}
                  data-testid="global-ai-nav-confirm-upsell"
                />
              ) : null}
            </Box>
          </Flex>
        </Flex>
      );
    }

    if (msg.kind === 'create_page_confirm') {
      // `create_custom_page` is terminal: it never builds. This chip / inline
      // trigger is the ONLY way the assistant builds + opens a custom dashboard,
      // and only on an explicit click — mirroring the nav-confirm hard rule.
      // Gate on the same Custom Dash access decision the single-chart "Open as
      // custom page" buttons use, so the chip is disabled + upsold when the
      // account lacks it.
      // Suppress the chip when the same turn's narration already carries a
      // RENDERABLE inline `offer:build` trigger stamped with this chip's id;
      // otherwise render the chip as the fallback.
      if (stampedOfferKindsByTargetId.get(msg.id)?.has('build')) {
        return null;
      }
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Flex
            direction="column"
            align="flex-end"
            gap={2}
            flex={1}
            minW={0}
            w="100%"
          >
            <Box maxW="100%">
              <Button
                {...CHIP_BUTTON_PROPS}
                className={styles.chip}
                data-testid="global-ai-create-page-confirm"
                maxW="100%"
                isDisabled={
                  !customDashAccess.allowed ||
                  msg.resolved ||
                  isTurnRunning ||
                  isCommandInFlight
                }
                onClick={
                  customDashAccess.allowed
                    ? () => fireBuildOffer(msg)
                    : undefined
                }
              >
                {customDashAccess.allowed
                  ? 'Build custom dashboard'
                  : `No access to ${customDashAccess.featureLabel}`}
              </Button>
              {!customDashAccess.allowed ? (
                <GlobalAssistantPermissionUpsell
                  access={customDashAccess}
                  data-testid="global-ai-create-page-confirm-upsell"
                />
              ) : null}
            </Box>
          </Flex>
        </Flex>
      );
    }

    if (msg.kind === 'report_offers') {
      // The report OFFER CARD is gone (product decision 2026-07-08): reports are
      // offered ONLY as inline [Name](offer:report) narration links now. This
      // message is kept purely as the DATA CARRIER those inline triggers resolve
      // against (`offerTargets.report` -> this id -> `msg.offers` ->
      // `matchReportOfferByMarkerText`), so it renders nothing itself.
      return null;
    }

    if (msg.kind === 'single_chart') {
      // A single-value answer renders headlessly as text (no chart card), the
      // same render path as a genuine text chart.
      const isTextChart =
        msg.item.plot?.chartType === 'text' || msg.answerAsText === true;

      if (isTextChart) {
        if (msg.answerAsText) {
          // Single-value answer: render ONLY the invisible (1px, headless)
          // preview slot so the chart can fetch the figure. The answer arrives
          // as the next text message, and the "Go to <page>" chip is rendered
          // BELOW that text answer (see the `text` branch) so it sits under the
          // answer rather than above it.
          return (
            <Box key={msg.id} w="100%" flexShrink={0}>
              <GlobalAIAssistantChartPreviewSlot
                messageId={msg.id}
                intersectionRoot={transcriptScrollRoot}
                variant="text"
              />
            </Box>
          );
        }
        return (
          <Box
            key={msg.id}
            alignSelf="stretch"
            maxW="100%"
            mb={3}
            w="100%"
            flexShrink={0}
          >
            <Flex align="flex-start" gap={2} w="100%">
              <AssistantAvatarSlot show={showAvatar} />
              <Box flex={1} minW={0} w="100%">
                <GlobalAIAssistantChartPreviewSlot
                  messageId={msg.id}
                  intersectionRoot={transcriptScrollRoot}
                  variant="text"
                />
              </Box>
            </Flex>
          </Box>
        );
      }

      return (
        <Box
          key={msg.id}
          alignSelf="stretch"
          maxW="100%"
          mb={3}
          w="100%"
          flexShrink={0}
        >
          {msg.intentSummary ? (
            <Flex align="flex-start" gap={2} mb={2}>
              <AssistantAvatarSlot show={showAvatar} />
              <Text
                fontSize="12px"
                lineHeight="15px"
                className={styles.text}
                flex={1}
                minW={0}
              >
                {msg.intentSummary}
              </Text>
            </Flex>
          ) : null}
          <GlobalAIAssistantChartPreviewSlot
            messageId={msg.id}
            intersectionRoot={transcriptScrollRoot}
          />
          {/* Hold the fallback chip until the turn settles: the chart appends
              before the narration streams, so rendering the chip immediately
              flashes a CTA that the arriving marker-bearing narration then
              suppresses. The chip is disabled mid-turn anyway, so deferring
              it costs nothing and removes the flash. */}
          {isTurnRunning ? null : (
            <Flex direction="column" align="flex-end" gap={2} mt={2} w="100%">
              {/* The post-chart chip is the last-resort fallback for the inline
                  offer:page trigger: it is suppressed ONLY when the page marker
                  stamped this chart AND that inline trigger will TRULY render —
                  i.e. the same access/applicability gate the trigger evaluates.
                  Otherwise (access denied → trigger falls back to plain text)
                  the chip and its upsell must remain, so suppression ==
                  renderability holds. */}
              {stampedOfferKindsByTargetId.get(msg.id)?.has('page') &&
              pageTriggerCanRender(msg)
                ? null
                : renderSingleChartStandardPageChip(msg)}
            </Flex>
          )}
        </Box>
      );
    }

    if (msg.kind === 'reasoning_trace') {
      // Skip empty finished traces (single text-only turn with no steps).
      if (msg.trace.steps.length === 0 && msg.trace.endedAt !== undefined) {
        return null;
      }
      return (
        <Flex
          key={msg.id}
          alignSelf="stretch"
          align="flex-start"
          gap={2}
          mb={3}
          maxW="100%"
        >
          <AssistantAvatarSlot show={showAvatar} />
          <Box flex={1} minW={0}>
            <ReasoningTraceView trace={msg.trace} />
          </Box>
        </Flex>
      );
    }

    return null;
  };

  return (
    <>
      {transcript.map((msg, index) =>
        renderMessage(msg, transcript[index - 1])
      )}
    </>
  );
}
